import styled from 'styled-components'
import { useRouter } from 'next/router'
import Image from 'next/legacy/image'
import { Typography } from 'common/typography'
import { dehydrate } from 'react-query/hydration'
import InternalError from 'errors/internal-error'
import NotFound from 'errors/not-found'
import Wrapper from 'common/wrapper'
import Navigation from 'components/navigation'
import Footer from 'common/footer'
import OpenGraph from 'common/open-graph'
import ProjectsPricing from 'projects/projects-pricing'
import SendToRevision from 'projects/send-to-revision'
import ProjectHero from 'projects/project-hero'
import { prefetchProject, useProject } from 'querys/projects'
import { getProjectService, getProjectsService } from 'services/projects'
import NewProjectsGrid from 'projects/new-projects-grid'
import FigmaForFrontends from 'projects/figma-for-frontends'
import { QueryClient } from 'react-query'
import flatEditorJsContent from 'utils/flat-editor-js-content'

const DetailImg = styled.div`
  position: relative;
  h2 {
    margin-block-end: 24px;
  }
  :before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: -2px;
    bottom: -2px;
    z-index: -1;
    transform: rotate(-180deg);
  }
  padding: 30px 0;
  img {
    width: 100%;
  }
`

function Project({ openGraph }) {
  const router = useRouter()
  const { data, isLoading, error } = useProject()

  if (router.isFallback) return <NotFound />
  if (isLoading) return null

  if (error?.message === 'User Unauthorized') return null
  if (error?.message === 'Not Found') return <NotFound />
  if (error?.message === 'Internal Error') return <InternalError />

  const {
    slug, cover_image: thumbnail, detail_image, video,
  } = data

  const {
    title, url, description, image,
  } = openGraph

  return (
  <>
    <OpenGraph
      title={title}
      url={url}
      description={description}
      image={image}
      keyword={`Cursos de Desarrollo Web, Leonidas Esteban, Proyectos de Frontend, Practicas de programación, Cómo programar ${slug}`}
    />
    <Navigation />
    <ProjectHero />
    <SendToRevision />
    <FigmaForFrontends />
    <DetailImg color="#021323" role="region" aria-labelledby="isometric">
      <Wrapper maxWidth="1246">
        <Typography as="h2" id="isometric" small="headline5" large="headline4">
          Proyección isométrica
        </Typography>
        <Image
          src={detail_image}
          alt={`Mockup del proyecto de ${title}`}
          width={1246}
          height={934}
        />
      </Wrapper>
    </DetailImg>
    <NewProjectsGrid similar />
    <ProjectsPricing />
    <Footer />
  </>
  )
}

Project.pageTheme = {
  background: 'var(--sky)',
}

export async function getStaticPaths() {
  const { count } = await getProjectsService({ countryCode: 'MX', page: 1 })
  const { results: projects } = await getProjectsService({ countryCode: 'MX', size: count })
  const paths = []
  projects.forEach(({ slug }) => paths.push({ params: { project: slug } }))
  return { paths, fallback: true }
}

export async function getStaticProps({ params: { project } }) {
  const queryClient = new QueryClient()
  const data = await getProjectService({ project })
  const queryClientProject = await prefetchProject(queryClient, project)
  return {
    props: {
      dehydratedState: dehydrate(queryClientProject),
      openGraph: {
        title: data?.title,
        url: `https://leonidasesteban.com/proyectos/${data?.slug}`,
        description: flatEditorJsContent(data?.description),
        image: data?.cover_image,
      }
    },
    revalidate: 60 * 60 * 24,
  }
}

export default Project
